/**
 * 前回選んだチームの記憶。
 *
 * 毎回チームを選び直すのは手間なので、選んだチームをブラウザに覚えさせる。保存先は
 * その人のブラウザの中だけで、他の人や他の端末には影響しない。
 *
 * プライベートウィンドウや保存を止めている設定では読み書き自体が例外になるため、
 * すべて try / catch で囲んで「覚えていない」に倒す（保存できないことより、画面が
 * 開かないことのほうが困る）。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 前回選んだチームをブラウザに覚えさせ、次に開いたときの初期表示に使う。
 * @graph-connects none
 */
import { logEvent } from '@cortex/faro';
import { TEAM_IDS, type TeamId } from './task.js';

/** 保存するときの名前。他のアプリと衝突しないよう接頭辞を付ける */
/** @graph-connects none */
export const REMEMBERED_TEAM_KEY = 'ado-task:team';

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 前回選んだチームを読む。覚えていない・読めない・知らない値のときは null を返す
 * @graph-connects none
 */
export function readRememberedTeam(): TeamId | null {
  try {
    const stored = window.localStorage.getItem(REMEMBERED_TEAM_KEY);
    return stored !== null && TEAM_IDS.includes(stored as TeamId) ? (stored as TeamId) : null;
  } catch (error: unknown) {
    // 読めなくても画面は開ける（チームを選ぶところから始まるだけ）。ただし黙って
    // 落とすと「毎回選ばされる」理由が分からなくなるので記録は残す
    logEvent('remembered_team_read_failed', {
      reason: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 選んだチームを覚える。保存できない環境では何もしない
 * @graph-connects none
 */
export function rememberTeam(team: TeamId): void {
  try {
    window.localStorage.setItem(REMEMBERED_TEAM_KEY, team);
  } catch (error: unknown) {
    // 保存できなくても画面は動く（次に開いたときにまた選んでもらう）。記録だけ残す
    logEvent('remembered_team_write_failed', {
      reason: error instanceof Error ? error.message : String(error),
    });
  }
}
